import React, { forwardRef, useContext } from "react";
import type { BaseProps } from "@purpur/common-types";
import { IconCheckmark } from "@purpur/icon/checkmark";
import { Paragraph } from "@purpur/paragraph";
import c from "classnames/bind";

import { ListboxNegativeContext } from "./listbox";
import styles from "./listbox.module.scss";

const cx = c.bind(styles);

const rootClassName = "purpur-listbox";

export type ListboxItemProps = Omit<BaseProps<"li">, "role"> & {
  highlighted?: boolean;
  hovered?: boolean;
  key?: string;
  selected?: boolean;
  disabled?: boolean;
  noninteractive?: boolean;
  negative?: boolean;
  /** When true, hides the built-in checkmark icon while preserving `aria-selected`. */
  hideSelectedIcon?: boolean;
};

export const ListboxItem = forwardRef<HTMLLIElement, ListboxItemProps>((props, ref) => {
  const listboxNegative = useContext(ListboxNegativeContext);
  const {
    disabled,
    highlighted,
    hovered,
    selected,
    children,
    className,
    noninteractive,
    negative,
    hideSelectedIcon,
    ...liProps
  } = props;
  const resolvedNegative = listboxNegative ?? negative;

  const classes = cx(`${rootClassName}-item`, className, {
    [`${rootClassName}-item--highlighted`]: highlighted,
    [`${rootClassName}-item--selected`]: selected,
    [`${rootClassName}-item--hovered`]: hovered,
    [`${rootClassName}-item--disabled`]: disabled,
    [`${rootClassName}-item--noninteractive`]: noninteractive,
    [`${rootClassName}-item--negative`]: resolvedNegative,
  });

  return (
    <li
      {...liProps}
      ref={ref}
      className={classes}
      aria-selected={!!selected}
      role="option"
      aria-disabled={!!disabled}
    >
      {typeof children === "string" ? (
        <Paragraph disabled={disabled} negative={resolvedNegative}>
          {children}
        </Paragraph>
      ) : (
        children
      )}
      {selected && !hideSelectedIcon && (
        <IconCheckmark size="xs" className={cx(`${rootClassName}-item__icon`)} />
      )}
    </li>
  );
});

ListboxItem.displayName = "ListBoxItem";
